import React, { useId, useMemo, useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ResponsiveContainer, AreaChart, Area, Tooltip } from "recharts";
import Navbar from "./Navbar";
import "../Static/LandingV2.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18, filter: "blur(8px)" },
  show: (d = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: d, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

function Section({ id, className = "", children }) {
  return (
    <section id={id} className={`cc-section ${className}`}>
      {children}
    </section>
  );
}

function SparkArea({ data, color = "#7C5CFF", animate = false }) {
  const uid = useId();
  const gradId = `cc-grad-${uid}`;

  return (
    <div className="cc-sparkWrap">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.55} />
              <stop offset="100%" stopColor={color} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <Tooltip cursor={false} content={() => null} />
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradId})`}
            dot={false}
            activeDot={false}
            isAnimationActive={animate}
            animationBegin={80}
            animationDuration={900}
            animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function MetricCard({ label, value, note, data, accent }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.45, once: true });

  return (
    <motion.div ref={ref} className="cc-metricCard" variants={fadeUp}>
      <div className="cc-metricTop">
        <div className="cc-metricK">{label}</div>
        <div className="cc-metricV">{value}</div>
      </div>
      <div className="cc-metricMid">
        {inView ? <SparkArea data={data} color={accent} animate /> : <div className="cc-sparkSkeleton" />}
      </div>
      <div className="cc-metricD">{note}</div>
    </motion.div>
  );
}

export default function LandingPage() {
  const reduceMotion = useReducedMotion();

  const metrics = useMemo(
    () => [
      {
        label: "Time saved / user / week",
        value: "6–10 hrs",
        note: "Automation + copilots reduce repetitive compliance work.",
        accent: "#7C5CFF",
        data: [{ v: 18 }, { v: 22 }, { v: 28 }, { v: 35 }, { v: 42 }, { v: 48 }, { v: 56 }, { v: 62 }],
      },
      {
        label: "Inspection prep cycle time",
        value: "-35%",
        note: "Faster Form 483 + FDA observation analytics and readiness.",
        accent: "#19D3FF",
        data: [{ v: 70 }, { v: 66 }, { v: 61 }, { v: 58 }, { v: 54 }, { v: 51 }, { v: 48 }, { v: 45 }],
      },
      {
        label: "Regulatory research time",
        value: "-45%",
        note: "Quicker answers across guidances, standards, and news.",
        accent: "#A78BFA",
        data: [{ v: 88 }, { v: 83 }, { v: 78 }, { v: 70 }, { v: 64 }, { v: 58 }, { v: 52 }, { v: 48 }],
      },
      {
        label: "Time-to-insight",
        value: "2×",
        note: "Search + extraction turns docs into decisions fast.",
        accent: "#34D399",
        data: [{ v: 30 }, { v: 34 }, { v: 41 }, { v: 47 }, { v: 55 }, { v: 61 }, { v: 68 }, { v: 75 }],
      },
    ],
    []
  );

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Code Carbon",
    url: "https://www.codecarbonhq.com/",
    email: "support@codecarbonhq.com",
  };

  return (
    <main className="cc-root">
      <Helmet prioritizeSeoTags>
        <title>Code Carbon | AI Engineering Excellence for Life Sciences</title>
        <meta
          name="description"
          content="Code Carbon builds AI-first intelligence platforms for Life Sciences—inspection analytics, regulatory intelligence, and enterprise-grade data systems."
        />
        <link rel="canonical" href="https://www.codecarbonhq.com/" />
        <meta property="og:site_name" content="Code Carbon" />
        <meta property="og:title" content="Code Carbon | AI Engineering Excellence for Life Sciences" />
        <meta
          property="og:description"
          content="Inspection intelligence and regulatory intelligence platforms—built with engineering excellence."
        />
        <meta property="og:url" content="https://www.codecarbonhq.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      <Navbar />

      {/* HERO */}
      <Section id="top" className="cc-hero">
        <div className="cc-heroGrid" aria-hidden="true" />
        <div className="cc-heroGlow" aria-hidden="true" />
        {!reduceMotion && <div className="cc-orbit" aria-hidden="true" />}

        <div className="cc-container">
          <motion.h1
            className="cc-title"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.6 }}
            variants={fadeUp}
          >
            Code Carbon builds AI systems for life sciences companies that feel inevitable.
          </motion.h1>

          <motion.p
            className="cc-subtitle"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.6 }}
            variants={fadeUp}
            custom={0.08}
          >
            Engineering excellence for regulated teams fast insights, audit-ready workflows, premium UX.
          </motion.p>

          <motion.div
            className="cc-ctaRow"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.6 }}
            variants={fadeUp}
            custom={0.16}
          >
            <a className="cc-btn cc-btnPrimary" href="#products">
              Explore products
            </a>
            <a className="cc-btn cc-btnGhost" href="mailto:support@codecarbonhq.com">
              Contact
            </a>
          </motion.div>
        </div>

        <a className="cc-scrollHint" href="#outcomes" aria-label="Scroll to outcomes">
          <span className="cc-scrollDot" />
          <span className="cc-scrollText">Scroll</span>
        </a>
      </Section>

      {/* OUTCOMES */}
      <Section id="outcomes" className="cc-dark">
        <div className="cc-container">
          <motion.div
            className="cc-kicker"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
          >
            Typical impact
          </motion.div>

          <motion.h2
            className="cc-h2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            variants={fadeUp}
            custom={0.06}
          >
            Less time searching. More time deciding.
          </motion.h2>

          <motion.div
            className="cc-metrics"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          >
            {metrics.map((m) => (
              <MetricCard key={m.label} {...m} />
            ))}
          </motion.div>
        </div>
      </Section>

      {/* PRODUCTS */}
      <Section id="products" className="cc-products">
        <div className="cc-container">
          <motion.h2
            className="cc-h2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            variants={fadeUp}
          >
            Intelligence platforms
          </motion.h2>

          <div className="cc-productGrid">
            <motion.a
              className="cc-productCard"
              href="https://atlas.codecarbonhq.com"
              target="_blank"
              rel="noreferrer"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              variants={fadeUp}
              custom={0.06}
            >
              <div className="cc-productBadge">Atlas</div>
              <div className="cc-productTitle">Inspection intelligence</div>
              <div className="cc-productDesc">
                Form 483s, FDA observations, inspection analytics, and readiness signals built for speed and clarity.
              </div>
              <div className="cc-productPills">
                <span>Form 483</span>
                <span>Observations</span>
                <span>Analytics</span>
                <span>Trends</span>
              </div>
              <div className="cc-productAction">Open Atlas →</div>
            </motion.a>

            <motion.a
              className="cc-productCard cc-productCardAlt"
              href="https://hedwig.codecarbonhq.com"
              target="_blank"
              rel="noreferrer"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              variants={fadeUp}
              custom={0.12}
            >
              <div className="cc-productBadge">Hedwig</div>
              <div className="cc-productTitle">Regulatory intelligence</div>
              <div className="cc-productDesc">
                Guidance documents, standards, and news. Organized, searchable, and continuously updated for regulated
                teams.
              </div>
              <div className="cc-productPills">
                <span>Guidances</span>
                <span>Standards</span>
                <span>News</span>
                <span>Alerts</span>
              </div>
              <div className="cc-productAction">Open Hedwig →</div>
            </motion.a>
          </div>
        </div>
      </Section>

      {/* CAPABILITIES */}
      <Section id="capabilities" className="cc-dark">
        <div className="cc-container">
          <motion.h2
            className="cc-h2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            variants={fadeUp}
          >
            Engineering excellence, delivered
          </motion.h2>

          <div className="cc-caps">
            {[
              { t: "AI copilots", d: "Assist users in regulated workflows with guardrails." },
              { t: "Doc intelligence", d: "Ingestion, extraction, search, and traceability." },
              { t: "Analytics UX", d: "Fast dashboards that turn signals into action." },
              { t: "Enterprise foundations", d: "Security, reliability, and scalable architecture." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                className="cc-capCard"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.35 }}
                variants={fadeUp}
                custom={i * 0.06}
              >
                <div className="cc-capTitle">{c.t}</div>
                <div className="cc-capDesc">{c.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section id="contact" className="cc-cta">
        <div className="cc-container">
          <motion.h2
            className="cc-h2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.55 }}
            variants={fadeUp}
          >
            Want this level of build quality?
          </motion.h2>

          <motion.div
            className="cc-ctaRow"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.55 }}
            variants={fadeUp}
            custom={0.08}
          >
            <a className="cc-btn cc-btnPrimary" href="mailto:support@codecarbonhq.com">
              support@codecarbonhq.com
            </a>
            <a className="cc-btn cc-btnGhost" href="#top">
              Back to top
            </a>
          </motion.div>
        </div>

        <div className="cc-footerLine">© {new Date().getFullYear()} Code Carbon</div>
      </Section>
    </main>
  );
}
